import type {
  CreateGroupRequest,
  Group,
  UpdateGroupRequest,
} from '../../api/group.service';
import type {
  CreateGroupEnrollmentRequest,
  GroupEnrollment,
  StudentEnrollmentSummary,
  UpdateGroupEnrollmentRequest,
} from '../../api/group-enrollment.service';
import type { DemoStoreData } from '../store-types';
import { route, type HandlerEntry } from '../handler-registry';

function newGroupId(data: DemoStoreData): string {
  return `grp-${data.groups.length + 1}-${Date.now().toString(36).slice(-4)}`;
}

function newEnrollmentId(data: DemoStoreData): string {
  return `enr-${data.enrollments.length + 1}-${Date.now().toString(36).slice(-4)}`;
}

export const groupsHandlers: readonly HandlerEntry[] = [
  route('GET', /^\/api\/tenants\/([^/]+)\/groups$/, (_ctx, data) => data.groups),
  route('GET', /^\/api\/tenants\/([^/]+)\/groups\/([^/]+)$/, (ctx, data) => {
    const id = decodeURIComponent(ctx.match[2]);
    const group = data.groups.find((g) => g.groupId === id);
    return group ?? { error: { status: 404, message: 'Not found' } };
  }),
  route('POST', /^\/api\/tenants\/([^/]+)\/groups$/, (ctx) => {
    const body = ctx.body as CreateGroupRequest;
    return {
      mutate: (data: DemoStoreData) => {
        const group: Group = {
          groupId: newGroupId(data),
          tenantId: data.tenant.tenantId,
          name: body.name,
          description: body.description ?? null,
          color: body.color ?? null,
          minAge: body.minAge ?? null,
          maxAge: body.maxAge ?? null,
          skillLevel: body.skillLevel ?? null,
          defaultCoachTenantAccountIds: body.defaultCoachTenantAccountIds ?? [],
          status: 'Active',
          notes: body.notes ?? null,
          monthlyFee: body.monthlyFee ?? null,
          createdAt: new Date().toISOString(),
          updatedAt: null,
        };
        return {
          next: { ...data, groups: [...data.groups, group] },
          result: group,
        };
      },
    };
  }),
  route('PATCH', /^\/api\/tenants\/([^/]+)\/groups\/([^/]+)$/, (ctx) => {
    const id = decodeURIComponent(ctx.match[2]);
    const body = ctx.body as UpdateGroupRequest;
    return {
      mutate: (data: DemoStoreData) => {
        const groups = data.groups.map((g) =>
          g.groupId === id
            ? {
                ...g,
                name: body.name ?? g.name,
                description: body.description === undefined ? g.description : body.description,
                color: body.color === undefined ? g.color : body.color,
                minAge: body.minAge === undefined ? g.minAge : body.minAge,
                maxAge: body.maxAge === undefined ? g.maxAge : body.maxAge,
                skillLevel: body.skillLevel === undefined ? g.skillLevel : body.skillLevel,
                defaultCoachTenantAccountIds:
                  body.defaultCoachTenantAccountIds ?? g.defaultCoachTenantAccountIds,
                status: body.status ?? g.status,
                notes: body.notes === undefined ? g.notes : body.notes,
                monthlyFee:
                  body.monthlyFee === undefined ? g.monthlyFee : body.monthlyFee,
                updatedAt: new Date().toISOString(),
              }
            : g,
        );
        const result = groups.find((g) => g.groupId === id)!;
        return { next: { ...data, groups }, result };
      },
    };
  }),
  route('DELETE', /^\/api\/tenants\/([^/]+)\/groups\/([^/]+)$/, (ctx) => {
    const id = decodeURIComponent(ctx.match[2]);
    return {
      mutate: (data: DemoStoreData) => ({
        next: {
          ...data,
          groups: data.groups.filter((g) => g.groupId !== id),
          enrollments: data.enrollments.filter((e) => e.groupId !== id),
        },
        result: undefined as void,
      }),
    };
  }),
  route(
    'GET',
    /^\/api\/tenants\/([^/]+)\/groups\/([^/]+)\/enrollments$/,
    (ctx, data) => {
      const groupId = decodeURIComponent(ctx.match[2]);
      return data.enrollments.filter((e) => e.groupId === groupId);
    },
  ),
  route(
    'POST',
    /^\/api\/tenants\/([^/]+)\/groups\/([^/]+)\/enrollments$/,
    (ctx) => {
      const groupId = decodeURIComponent(ctx.match[2]);
      const body = ctx.body as CreateGroupEnrollmentRequest;
      return {
        mutate: (data: DemoStoreData) => {
          const enrollment: GroupEnrollment = {
            enrollmentId: newEnrollmentId(data),
            tenantId: data.tenant.tenantId,
            groupId,
            studentTenantAccountId: body.studentTenantAccountId,
            status: 'Active',
            startDate: body.startDate ?? new Date().toISOString().slice(0, 10),
            endDate: null,
            notes: body.notes ?? null,
            monthlyFeeOverride: body.monthlyFeeOverride ?? null,
            createdAt: new Date().toISOString(),
            updatedAt: null,
          };
          return {
            next: { ...data, enrollments: [...data.enrollments, enrollment] },
            result: enrollment,
          };
        },
      };
    },
  ),
  route(
    'PATCH',
    /^\/api\/tenants\/([^/]+)\/groups\/([^/]+)\/enrollments\/([^/]+)$/,
    (ctx) => {
      const enrollmentId = decodeURIComponent(ctx.match[3]);
      const body = ctx.body as UpdateGroupEnrollmentRequest;
      return {
        mutate: (data: DemoStoreData) => {
          const enrollments = data.enrollments.map((e) =>
            e.enrollmentId === enrollmentId
              ? {
                  ...e,
                  status: body.status ?? e.status,
                  startDate: body.startDate === undefined ? e.startDate : body.startDate ?? e.startDate,
                  endDate: body.endDate === undefined ? e.endDate : body.endDate,
                  notes: body.notes === undefined ? e.notes : body.notes,
                  monthlyFeeOverride: body.clearMonthlyFeeOverride
                    ? null
                    : body.monthlyFeeOverride === undefined
                      ? e.monthlyFeeOverride
                      : body.monthlyFeeOverride,
                  updatedAt: new Date().toISOString(),
                }
              : e,
          );
          const result = enrollments.find((e) => e.enrollmentId === enrollmentId)!;
          return { next: { ...data, enrollments }, result };
        },
      };
    },
  ),
  route(
    'DELETE',
    /^\/api\/tenants\/([^/]+)\/groups\/([^/]+)\/enrollments\/([^/]+)$/,
    (ctx) => {
      const enrollmentId = decodeURIComponent(ctx.match[3]);
      return {
        mutate: (data: DemoStoreData) => ({
          next: {
            ...data,
            enrollments: data.enrollments.filter((e) => e.enrollmentId !== enrollmentId),
          },
          result: undefined as void,
        }),
      };
    },
  ),
  route(
    'GET',
    /^\/api\/tenants\/([^/]+)\/students\/([^/]+)\/enrollments$/,
    (ctx, data): readonly StudentEnrollmentSummary[] => {
      const studentId = decodeURIComponent(ctx.match[2]);
      return data.enrollments
        .filter((e) => e.studentTenantAccountId === studentId)
        .map((e) => ({
          ...e,
          groupName: data.groups.find((g) => g.groupId === e.groupId)?.name ?? null,
        }));
    },
  ),
];
